<template>
    <div class="relative group py-2 min-h-[90px] px-4 flex items-center" @mouseup="closeDatePickerOnMouseUp">
        <div class="-m-1 flex-1 block p-1 w-full">
            <div aria-hidden="true"
                 class="absolute inset-0 group-hover:bg-gray-50 bg-white-50"></div>
            <div class="flex-1 flex items-center min-w-0 relative">
                <div class="m-0 w-full">
                    <div class="flex w-full">
                        <div class="text-gray-500 mt-px items-start flex mr-3"
                             v-if="status === 'Todo' || (completed && !completedInPast)">
                            <a @click.prevent="toggleComplete"
                               class="text-2xl text-brand hover:text-brand-light mt-1.5">
                                <i v-if="status==='Done'" class="far fa-check-square"></i>
                                <i v-else class="far fa-square"></i>
                            </a>
                        </div>
                        <div class="text-gray-500 mt-px items-start flex mr-3" v-else>
                            <a class="text-2xl text-gray-300 hover:text-gray-300 mt-1.5"
                               @click.prevent="toggleComplete">
                                <i class="far fa-check-square"></i>
                            </a>
                        </div>
                        <div class="flex-grow">
                            <div class="flex w-full truncate">
                                <div class="text-sm font-medium text-gray-900 w-full"
                                     @mouseenter="waitForMouseUp = true" @mouseleave="waitForMouseUp = false"
                                     @mouseup="editing('description')">
                                    <textarea-autosize v-model="description" ref="description"
                                                       rows="1"
                                                       :readonly="(isEditing !== 'description') || (status==='Done')"
                                                       :style="'cursor: '+(status!=='Todo'?'default':'text')+' !important'"
                                                       class="m-0 text-lg px-1 py-0 focus:ring-0 ring-0 shadow-none border-1 rounded-md"
                                                       :class="isEditing?'bg-white border-gray-200':'bg-transparent border-transparent hover:border-transparent '"
                                                       @input="isEditing?save():false"
                                                       :placeholder="(isEditing?'':'...') + ''"
                                                       @focus.native="(e) => { if (status!=='Todo'){ e.target.blur();}}"
                                                       @blur.native="stopEditing"></textarea-autosize>
                                </div>
                            </div>
                            <div class="text-xs ml-0 mt-1" v-if="packageReference || isEditing">
                                <div v-if="isEditing" ref="package" @mouseenter="waitForMouseUp = true"
                                     @mouseleave="waitForMouseUp = false">
                                    <ui-dropdown placement="bottom-start" menu-class="w-full max-w-lg"
                                                 @closed="packageFinished"
                                                 ref="inlineSearch">
                                        <template #default="{ open, close }">
                                            <div class="flex w-full items-center">
                                                <page-cell name="search"
                                                           v-model="search"
                                                           ref="searchBox"
                                                           @keydown.esc.stop="search = ''"
                                                           @input="openInlineSearch(open, close)"
                                                           @focus="openInlineSearch(open,close)"
                                                           @keydown.enter=""
                                                           :placeholder="packageName || 'Add package...'"
                                                           :style="'box-shadow:none !important; overflow:hidden'"
                                                           class="font-semibold m-0 p-1 rounded-md text-gray-500 text-xs border-1 border-gray-200 resize-none outline-none shadow-none ring-0 bg-transparent w-full bg-white hover:border-gray-400">
                                                </page-cell>
                                                <span class="text-gray-500 fa fa-times pl-1 hover:text-blue-500 ml-0.5"
                                                      tabindex="-1"
                                                      v-if="search"
                                                      @mouseup="packageCleared(close)"></span>
                                            </div>
                                        </template>
                                        <template #menu="{ close }">
                                            <div class="p-3 max-h-64 overflow-y-auto" v-if="search">
                                                <div v-if="searchedPackages.length">
                                                    <div v-for="p in searchedPackages">
                                                        <div
                                                            class="relative group py-2 px-2 flex items-center hover:bg-gray-50 rounded-lg"
                                                            @mouseup.stop.prevent="packageSelected(p, close)">
                                                            <div class="flex-1 flex items-center min-w-0 relative">
                                                                <div class="ml-0 w-full truncate">
                                                                    <div
                                                                        class="text-xs font-medium text-gray-900 flex w-full">
                                                                        <div class="flex-grow truncate"
                                                                             v-text="p.name"></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="p-3 max-h-64 overflow-y-auto" v-else>
                                                    No matching Packages found!
                                                </div>
                                            </div>
                                            <div class="p-3 max-h-64 overflow-y-auto" v-else>
                                                Start typing to search for packages...
                                            </div>
                                        </template>
                                    </ui-dropdown>
                                </div>
                                <a class="font-semibold mr-0.5 text-brand hover:text-brand-light"
                                   v-else-if="packageReference"
                                   :href="packageUrl(packageReference)" v-text="packageName"></a>
                            </div>
                            <div class="text-xs ml-0 mt-1" v-if="isEditing || due || completedInPast">
                                <div v-if="isEditing">
                                    <ui-dropdown menu-class="px-4 py-2 w-72" placement="bottom" :offset="[160,8]"
                                                 @closed="closedReminders"
                                                 @mousedown="editing('reminder')"
                                                 ref="inlineNotifications">
                                        <template #default="{ toggle, open, close}">
                                            <div class="flex w-full truncate text-xs ml-0"
                                                 ref="dueDateTime">
                                                <div class="flex-grow text-xs truncate">
                                                    <div class="flex truncate items-baseline">
                                                        <span @click="editing('date')"
                                                              class="text-xs font-semibold mr-0.5 text-gray-500">Due :</span>
                                                        <div @mousedown="editing('date')"
                                                             :class="'flex-1 mr-0 text-xs border-none border-opacity-75'">
                                                            <ui-date-picker ref="datepicker" :with-time="true"
                                                                            v-model="due"
                                                                            format="Y-m-d H:i:ss"
                                                                            field="m-0 text-xs py-0.5 px-1 focus:ring-0 ring-0 shadow-none rounded-md bg-white border-1 bg-white hover:border-gray-400 border-gray-200"
                                                                            placeholder="Due date/time"
                                                                            class="truncate text-gray-500"
                                                                            @input="save"

                                                                            @close="closingDatePicker"
                                                            ></ui-date-picker>
                                                        </div>
                                                        <div @mouseenter="waitForMouseUp = true"
                                                             @mouseleave="waitForMouseUp = false">
                                                            <span
                                                                class="text-gray-500 fa fa-bell pl-1 hover:text-blue-500 ml-0.5"
                                                                tabindex="-1"
                                                                v-if="due"
                                                                @mouseup.prevent.stop="open()"></span>
                                                        </div>
                                                        <span
                                                            class="text-gray-500 fa fa-times pl-1 hover:text-blue-500 ml-2"
                                                            tabindex="-1"
                                                            v-if="due"
                                                            @mouseup="dateTimeCleared()"></span>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <template #menu="{ close }">
                                            <div
                                                class="text-base text-gray-400 w-full border-1 border-bottom border-gray-50">
                                                Send notification:
                                            </div>
                                            <div :key="toggleKey">
                                                <ui-dropdown-item
                                                    :ticked="notifications[0]"
                                                    @click="toggleNotification(0)">At due time of reminder
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[5]"
                                                    @click="toggleNotification(5)">5 minutes before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[10]"
                                                    @click="toggleNotification(10)">10 minutes before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[15]"
                                                    @click="toggleNotification(15)">15 minutes before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[30]"
                                                    @click="toggleNotification(30)">30 minutes before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[60]"
                                                    @click="toggleNotification(60)">1 hour before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[120]"
                                                    @click="toggleNotification(120)">2 hours before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[1440]"
                                                    @click="toggleNotification(1440)">1 day before
                                                </ui-dropdown-item>
                                                <ui-dropdown-item
                                                    :ticked="notifications[2880]"
                                                    @click="toggleNotification(2880)">2 days before
                                                </ui-dropdown-item>
                                                <ui-dropdown-divider></ui-dropdown-divider>
                                                <ui-dropdown-item
                                                    @click="clearNotifications()">Remove all notifications
                                                </ui-dropdown-item>
                                            </div>
                                        </template>
                                    </ui-dropdown>
                                </div>
                                <div class="flex w-full truncate text-xs ml-0 items-baseline" v-if="!isEditing && due">
                                    <div class="truncate text-gray-500 flex-1 mr-1 mt-1 flex-grow"
                                         v-text="formatDate(due)"></div>
                                    <span class="text-gray-500 fa fa-bell pl-1 hover:text-blue-500 ml-0.5"
                                          v-if="hasNotifications()"></span>
                                </div>
                                <div class="flex w-full truncate text-xs ml-0" v-if="completedInPast">
                                    <div class="flex-grow text-xs truncate">
                                        <div class="flex truncate items-baseline">
                                                <span
                                                    class="text-xs font-semibold mr-0.5 text-gray-500">Completed :</span>
                                            <span class="truncate text-gray-500"
                                                  v-text="formatDate(completed)"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>


import moment from "moment-timezone";
import UiInput from "../../ui/UiInput.vue";
import UiSelectSearchable from "../../ui/UiSelectSearchable.vue";
import PageCell from "../../ui/PageCell.vue";
import MessageWidgetChannelAvatars from "../MessageWidget/MessageWidgetChannelAvatars.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import {Reminder} from "@models";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";

export default {
    components: {
        UiDropdownDivider,
        UiDropdownItem,
        UiDropdown, MessageWidgetChannelAvatars, PageCell, UiSelectSearchable, UiInput
    },
    props: {
        item: Object,
        packages: []
    },
    data() {
        return {
            description: this.item.description,
            due: this.item.due,
            status: this.item.status,
            packageName: this.item.packageName,
            packageReference: this.item.packageReference,
            completed: this.item.completed,
            isEditing: false,
            waitForMouseUp: false,
            search: this.item.packageName,
            saveTimeout: false,
            notifications: Object.fromEntries(this.item.notifications.map(value => [value, true])),
            toggleKey: 0,
        }
    },
    computed: {
        completedInPast() {
            if (this.status === 'Done') {
                return (Math.abs(moment().diff(moment(this.completed), 'seconds'))) > 5
            }
            return false
        },
        searchedPackages() {
            const query = this.search.trim().toLowerCase().split(' ')
            return this.packages.filter((item) => {
                return query.every((token) => {
                    return item.tokens.includes(token);
                })
            }).slice(0, 50)
        }
    },
    methods: {
        assignFromItem(item) {
            this.description = item.description
            this.due = item.due
            this.status = item.status
            this.packageName = item.packageName
            this.completed = item.completed
            this.packageReference = item.packageReference
            this.notifications = Object.fromEntries(item.notifications.map(value => [value, true]))
        },
        packageFinished() {
            this.search = this.packageName
            this.waitForMouseUp = true
            setTimeout(() => {
                this.$nextTick(() => {
                    this.$nextTick(() => {
                        this.closeDatePickerOnMouseUp()
                    })
                })
            }, 100)
        },
        packageCleared(close) {
            this.packageName = null
            this.packageReference = null
            close();
            this.search = ''
            this.$refs.description.$el.focus()
            this.save()
        },
        dateTimeCleared() {
            this.due = false
            this.$refs.description.$el.focus()
            this.save()
        },
        packageSelected(p, close) {
            this.packageName = p.label;
            this.packageReference = p.reference;
            this.search = p.label;
            this.$refs.description.$el.focus()
            close();
            this.save()
        },
        openInlineSearch(open, close) {
            this.$refs.inlineNotifications.close()
            open();
        },
        closingDatePicker() {
            if (!this.waitForMouseUp) {
                this.editing(false)
            } else {
                this.closeDatePicker = true
            }
        },
        closeDatePickerOnMouseUp() {
            if (this.waitForMouseUp) {
                this.waitForMouseUp = false
                if (this.$refs.description.$el === document.activeElement) {
                    return
                } else if (this.$refs.datepicker && this.$refs.datepicker.$el.contains(document.activeElement)) {
                    return
                } else if (this.$refs.package && this.$refs.package.contains(document.activeElement)) {
                    return
                }
                this.editing(false)
            }
        },
        closedReminders(){
            this.editing(this.isEditing)
        },
        stopEditing(e) {
            this.$nextTick(() => {
                if (this.$refs.dueDateTime && this.$refs.dueDateTime.contains(e.relatedTarget)) {
                    return
                } else if (this.$refs.package && this.$refs.package.contains(e.relatedTarget)) {
                    return
                } else if (this.$refs.description && this.$refs.description.$el.contains(e.relatedTarget)) {
                    return
                }
                this.editing(false)
            })
        },
        editing(ty) {
            if (this.status === 'Done') {
                return;
            }
            this.save()
            this.isEditing = ty
            if (!ty) {
                this.$emit("editFinished", this.item)
                if (!this.description && !this.due && !this.packageReference) {
                    if (this.item.isNew) {
                    } else {
                        Reminder.delete(this.item.id)
                    }
                }
            }
            this.$nextTick(() => {
                this.$nextTick(() => {
                    if (ty === 'description' && this.$refs.description) {
                        this.$refs.description.$el.focus()
                    } else if (ty === 'date' && this.$refs.datepicker) {
                        this.$refs.inlineNotifications.close()
                        this.$refs.datepicker.flatpickr.open()
                    }
                })
            });
        },
        formatDate(due) {
            let d = moment(due)
            return d.format('MMMM Do, YYYY [at] hh:mmA')
        },
        packageUrl(packageReference) {
            return route('mobile.packages.byReference.dashboard', {packageReference});
        },
        toggleComplete() {
            if (this.status === 'Done') {
                this.status = 'Todo'
                this.completed = false
                Reminder.markTodo(this.item.id)
            } else {
                this.status = 'Done'
                this.completed = moment()
                Reminder.markDone(this.item.id)
            }
        },
        async save() {
            clearTimeout(this.saveTimeout);
            this.saveTimeout = setTimeout(() => this.doSave(), 350)
        },
        async doSave() {
            let notf = Object.entries(this.notifications).filter(value => value[1]).map(value => value[0]);

            let original = {
                description: this.item.description,
                due: this.item.due,
                status: this.item.status,
                packageName: this.item.packageName,
                packageReference: this.item.packageReference,
                notifications: this.item.notifications,
            }
            let data = {
                description: this.description,
                due: this.due,
                status: this.status,
                packageName: this.packageName,
                packageReference: this.packageReference,
                notifications: notf
            };
            if (this.item.isNew) {
                if ((this.description || this.due || this.packageReference)) {
                    this.item.isNew = false
                    Reminder.create(this.item.id, data)
                }
            } else if (JSON.stringify(original) !== JSON.stringify(data)) {
                Reminder.update(this.item.id, data)
            }
        },
        toggleNotification(time) {
            this.notifications[time] = !this.notifications[time];
            this.toggleKey++;
            this.doSave()
        },
        clearNotifications() {
            this.notifications = {};
            this.toggleKey++;
            this.doSave()
        },
        hasNotifications() {
            return Object.entries(this.notifications).some(value => value[1])
        }
    },
    async mounted() {
    }
}
</script>
